import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import {
  GuiaBreadcrumb,
  ConversionBlock,
  PriceTag,
  GuiaFAQ,
  GuiaRelacionadas,
  SchemaArticle,
  SchemaFAQPage,
} from '../components/GuiaComponents';
import ReassuranceSection from '@/components/feature/ReassuranceSection';

const FAQS = [
  {
    question: '¿Cuánto cuesta cambiar el motor de un toldo en Málaga?',
    answer:
      'El precio depende del tipo de motor y de la complejidad de la instalación. Un motor tubular estándar para toldo doméstico ronda los 180-280 €, instalación incluida. Si además hay que cambiar el cableado, añadir un sensor de viento o reprogramar los finales de carrera, el precio puede llegar a los 350-450 €. Siempre damos presupuesto gratuito antes de empezar.',
  },
  {
    question: '¿Cómo saber si el motor del toldo está quemado?',
    answer:
      'Los síntomas son bastante claros: el toldo no se mueve en ninguna dirección, el motor no emite ningún sonido al pulsar el mando, o emite un zumbido pero no gira. A veces también notas olor a quemado cerca del cajón del toldo. Si el toldo funciona pero va muy lento o se para a medias, el problema puede ser del condensador, no del motor completo.',
  },
  {
    question: '¿Puedo cambiar yo mismo el motor de un toldo eléctrico?',
    answer:
      'Rotundamente no lo recomendamos. Cambiar el motor implica manipular el cableado eléctrico (220V), desmontar el tubo de enrollado (que pesa bastante), extraer el motor antiguo, insertar el nuevo en la posición exacta, configurar los finales de carrera y sincronizar el mando. Un error en cualquiera de estos pasos puede provocar un cortocircuito, dañar la lona o dejar el toldo inutilizado.',
  },
  {
    question: '¿Merece la pena motorizar un toldo manual?',
    answer:
      'En nuestra experiencia, sí, especialmente en Málaga. Un toldo motorizado lo extiendes y recoges con un mando en segundos, lo que hace que lo uses mucho más. Además, puedes añadir un sensor de viento que recoja el toldo automáticamente cuando hay rachas fuertes —algo muy útil con el levante malagueño— y un sensor de sol para que se extienda solo. La motorización cuesta entre 250 y 400 €, instalación incluida.',
  },
];

export default function GuiaCambiarMotorToldo() {
  return (
    <>
      <title>Cambiar el motor de un toldo eléctrico | Toldos Málaga</title>
      <meta
        name="description"
        content="¿Necesitas cambiar el motor de tu toldo eléctrico? Guía completa: diagnóstico, tipos de motor, precios y reparación profesional en Málaga. Motorización de toldos manuales. Presupuesto gratis."
      />
      <meta
        name="keywords"
        content="motor toldo, como cambiar el motor de un toldo, instalar motor toldo, motorizar toldo malaga, reparar motor toldo electrico malaga"
      />
      <link rel="canonical" href="https://toldos-malaga.es/guias/cambiar-motor-toldo" />
      <SchemaArticle
        headline="Cambiar el motor de un toldo eléctrico: guía completa"
        description="Todo sobre el cambio y la instalación de motores para toldos eléctricos. Diagnóstico de averías, tipos de motor, sensor de viento, precios en Málaga y motorización de toldos manuales."
        datePublished="2025-11-24"
        dateModified="2026-06-16"
        imageUrl="/images/016-guia-cambiar-motor-og.webp"
        canonicalUrl="https://toldos-malaga.es/guias/cambiar-motor-toldo"
      />
      <SchemaFAQPage faqs={FAQS} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Cambio de motor de toldo eléctrico en Málaga',
              description: 'Sustitución profesional del motor tubular de toldos eléctricos y motorización de toldos manuales. Instalación de sensores de viento y sol en Málaga.',
            },
            priceSpecification: {
              '@type': 'PriceSpecification',
              price: 180,
              priceCurrency: 'EUR',
            },
          }),
        }}
      />
      <Navbar />
      <main>
        <GuiaBreadcrumb currentTitle="Cambiar el motor de un toldo eléctrico" />

        <section className="w-full max-w-[1280px] mx-auto px-4 md:px-6 pt-6 pb-10">
          <div className="relative rounded-2xl overflow-hidden h-[280px] md:h-[380px]">
            <img
              src="/images/015-guia-cambiar-motor-hero.webp"
              alt="Técnico reparando el motor de un toldo eléctrico en Málaga"
              title="Cambio de motor de toldo eléctrico en Málaga"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/15 to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
              <span className="inline-block px-3 py-1 rounded-full bg-accent-500/90 text-foreground-900 text-xs font-semibold mb-3">
                Guía de reparación
              </span>
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-white">
                Cambiar el motor de un toldo eléctrico
              </h1>
            </div>
          </div>
        </section>

        <ReassuranceSection />

        <article className="w-full max-w-4xl mx-auto px-4 md:px-6 pb-14">
          <div className="prose-custom space-y-6 text-foreground-700 leading-relaxed">

            <p className="text-lg text-foreground-600">
              Pulsas el mando y no pasa nada. O el toldo empieza a moverse pero se para a mitad de camino. O hace un ruido raro que antes no hacía. Cuando el motor de un toldo eléctrico falla, la comodidad de tu terraza se esfuma de golpe. En esta guía te explicamos cómo diagnosticar el problema, qué opciones tienes y cuánto cuesta la solución en Málaga.
            </p>

            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10">
              Diagnóstico: ¿está roto el motor o es otra cosa?
            </h2>

            <p>
              Antes de asumir que el motor está muerto, descarta estos problemas —mucho más baratos de solucionar— que dan síntomas parecidos:
            </p>

            <div className="space-y-4">
              <div className="flex gap-3">
                <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-primary-600 text-xs font-bold">1</span>
                </div>
                <div>
                  <strong className="text-foreground-900">Mando sin pilas o desincronizado.</strong>
                  <p className="text-sm text-foreground-600 mt-1">
                    Parece una tontería, pero es sorprendentemente frecuente. Cambia las pilas del mando primero. Si sigue sin funcionar, prueba a resincronizarlo siguiendo las instrucciones del fabricante (suele ser pulsar un botón en el receptor del motor mientras mantienes pulsado el mando).
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-primary-600 text-xs font-bold">2</span>
                </div>
                <div>
                  <strong className="text-foreground-900">Condensador quemado.</strong>
                  <p className="text-sm text-foreground-600 mt-1">
                    El condensador es una pieza pequeña y barata (15-30 €) que da el impulso inicial al motor. Si oyes un zumbido pero el toldo no gira, probablemente sea el condensador. Es una reparación sencilla y mucho más económica que cambiar el motor entero.
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-primary-600 text-xs font-bold">3</span>
                </div>
                <div>
                  <strong className="text-foreground-900">Finales de carrera desprogramados.</strong>
                  <p className="text-sm text-foreground-600 mt-1">
                    Si el toldo se para antes de tiempo o no llega a la posición correcta, los finales de carrera —que le dicen al motor cuándo parar— se han desajustado. Se reprograman en 15 minutos sin cambiar ninguna pieza.
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-primary-600 text-xs font-bold">4</span>
                </div>
                <div>
                  <strong className="text-foreground-900">Cableado dañado por humedad.</strong>
                  <p className="text-sm text-foreground-600 mt-1">
                    En Málaga, la humedad ambiental y la cercanía al mar pasan factura a las conexiones eléctricas exteriores. Un cable pelado o un empalme oxidado puede cortar la corriente. Revisa las conexiones visibles antes de tocar nada más.
                  </p>
                </div>
              </div>
            </div>

            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10">
              Señales claras de que el motor ha muerto
            </h2>

            <p>
              Si has descartado todo lo anterior y te encuentras con estos síntomas, el motor probablemente ha llegado al final:
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="p-4 rounded-xl bg-secondary-100/70 border border-secondary-300/60">
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-close-circle-line text-red-500 text-lg"></i>
                  <span className="font-semibold text-foreground-900 text-sm">Silencio total</span>
                </div>
                <p className="text-xs text-foreground-600">
                  El toldo no se mueve en absoluto y el motor no emite ningún zumbido ni vibración. Completamente muerto.
                </p>
              </div>
              <div className="p-4 rounded-xl bg-secondary-100/70 border border-secondary-300/60">
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-fire-line text-red-500 text-lg"></i>
                  <span className="font-semibold text-foreground-900 text-sm">Olor a quemado</span>
                </div>
                <p className="text-xs text-foreground-600">
                  Si hueles a cable quemado cerca del cajón del toldo, el bobinado interno del motor se ha sobrecalentado y quemado.
                </p>
              </div>
              <div className="p-4 rounded-xl bg-secondary-100/70 border border-secondary-300/60">
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-speed-mini-line text-red-500 text-lg"></i>
                  <span className="font-semibold text-foreground-900 text-sm">Va muy lento</span>
                </div>
                <p className="text-xs text-foreground-600">
                  El toldo se mueve a cámara lenta y se nota que le cuesta. Los rodamientos internos están desgastados y el motor está al límite.
                </p>
              </div>
              <div className="p-4 rounded-xl bg-secondary-100/70 border border-secondary-300/60">
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-shuffle-line text-red-500 text-lg"></i>
                  <span className="font-semibold text-foreground-900 text-sm">Va a tirones</span>
                </div>
                <p className="text-xs text-foreground-600">
                  El toldo avanza a sacudidas, como si le costara mantener un ritmo constante. El reductor del motor está fallando.
                </p>
              </div>
            </div>

            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10">
              ¿Cambiar el motor yo mismo? Ni se te ocurra
            </h2>

            <p>
              Vamos a ser directos: cambiar un motor de toldo es una reparación de nivel avanzado que implica trabajar con electricidad y manipular piezas pesadas en altura. No es como cambiar un enchufe. Esto es lo que implica realmente:
            </p>

            <div className="space-y-3">
              <div className="flex gap-2">
                <i className="ri-flashlight-line text-red-500 mt-0.5 flex-shrink-0"></i>
                <p className="text-sm text-foreground-600">
                  <strong className="text-foreground-800">Riesgo eléctrico real.</strong> El motor va conectado a la red de 220V de tu vivienda. Un mal empalme puede provocar un cortocircuito o, peor, una descarga. Cortar el diferencial no basta si no sabes exactamente qué cable estás tocando.
                </p>
              </div>
              <div className="flex gap-2">
                <i className="ri-scales-3-line text-red-500 mt-0.5 flex-shrink-0"></i>
                <span className="text-sm text-foreground-600">
                  <strong className="text-foreground-800">El tubo de enrollado pesa.</strong> Para extraer el motor viejo hay que desmontar el tubo donde va alojado. Ese tubo, con la lona enrollada, puede pesar más de 20 kg. Si se te escapa, adiós lona y adiós suelo de la terraza.
                </span>
              </div>
              <div className="flex gap-2">
                <i className="ri-settings-3-line text-red-500 mt-0.5 flex-shrink-0"></i>
                <span className="text-sm text-foreground-600">
                  <strong className="text-foreground-800">Configuración de finales de carrera.</strong> Cada motor nuevo viene sin configurar. Hay que programar manualmente hasta dónde debe extenderse y recogerse el toldo. Un error de programación hace que el toldo fuerce contra la pared o no se enrolle del todo, dañando el motor recién instalado.
                </span>
              </div>
            </div>

            <div className="my-8">
              <ConversionBlock
                title="¿Motor de toldo estropeado en Málaga? Lo cambiamos rápido"
                subtitle="Instalamos motores tubulares de primeras marcas con garantía. También motorizamos toldos manuales. Presupuesto gratis y respuesta en 24/48h."
              />
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 my-8">
              <PriceTag price="desde 180 €" description="Cambio motor estándar" />
              <PriceTag price="desde 250 €" description="Motorización toldo manual" />
              <p className="text-sm text-foreground-500">
                Precios orientativos con instalación incluida. Varía según potencia del motor y accesorios adicionales (sensor de viento, mando múltiple).
              </p>
            </div>

            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10">
              ¿Y si aprovecho para motorizar mi toldo manual?
            </h2>

            <p>
              Si tienes un toldo de manivela y estás harto de darle vueltas cada mañana y cada noche, motorizarlo es una inversión que cambia la experiencia por completo. En Málaga, donde el toldo se usa a diario durante al menos 8 meses al año, la diferencia es aún más notable.
            </p>

            <p>
              El proceso consiste en retirar el mecanismo de manivela, insertar un motor tubular dentro del eje de enrollado, pasar el cableado hasta una toma de corriente cercana y configurar el mando. Se puede añadir opcionalmente un sensor de viento —muy recomendable en la costa malagueña— que recoge automáticamente el toldo cuando detecta rachas fuertes, protegiendo la estructura de roturas por levante.
            </p>

            <p>
              El precio de motorizar un toldo manual ronda los 250-400 €, instalación y programación incluidas. La inversión se amortiza en comodidad y en evitar reparaciones por olvidos: ¿cuántas veces has dejado el toldo extendido y el viento lo ha castigado?
            </p>

            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10">
              Preguntas frecuentes
            </h2>

            <GuiaFAQ faqs={FAQS} />

            <div className="mt-12">
              <GuiaRelacionadas />
            </div>

          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}